import { Button, Combobox, type ComboboxOption, DateRangeField, MultiSelect, StringSelect } from "@tellescope/ui";
import { useUsersQuery } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getTicketFreeformFieldOptions, type TicketFilters, type TicketFreeformFilter } from "./ticketFilters";
import { getStageOptions, getStatusOptions, getTagsBehaviorOptions } from "./ticketFilterOptions";
import { FreeformFilterRow } from "../tables";

export interface FilterConfigFormProps {
  stagedFilters: TicketFilters;
  onStagedFiltersChange: (filters: TicketFilters) => void;
  isDefaultView?: boolean;
  currentUserId?: string;
}

export const FilterConfigForm = ({
  stagedFilters,
  onStagedFiltersChange,
  isDefaultView,
  currentUserId,
}: FilterConfigFormProps) => {
  const { t } = useTranslation("tickets");

  const { data: users = [] } = useUsersQuery();

  const userComboboxOptions = useMemo<ComboboxOption[]>(
    () =>
      users.map((user) => ({
        value: user.id,
        label: formatName(user) || user.id,
      })),
    [users]
  );

  const setFilterField = <K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => {
    onStagedFiltersChange({ ...stagedFilters, [key]: value });
  };

  const tagMultiSelectOptions = useMemo(() => {
    const existing = stagedFilters.tags?.tags ?? [];
    return existing.map((tag) => ({ value: tag, label: tag }));
  }, [stagedFilters.tags?.tags]);

  const handleAddFreeformFilter = () => {
    onStagedFiltersChange({
      ...stagedFilters,
      freeform: [
        ...(stagedFilters.freeform ?? []),
        { field: getTicketFreeformFieldOptions()[0]?.value ?? "", operation: "equals", value: "" },
      ],
    });
  };

  const handleUpdateFreeformFilter = (index: number, updated: TicketFreeformFilter) => {
    const freeform = [...(stagedFilters.freeform ?? [])];
    freeform[index] = updated;
    onStagedFiltersChange({ ...stagedFilters, freeform });
  };

  const handleRemoveFreeformFilter = (index: number) => {
    const freeform = (stagedFilters.freeform ?? []).filter((_, i) => i !== index);
    onStagedFiltersChange({ ...stagedFilters, freeform });
  };

  const selectedOwnerOptions = useMemo<ComboboxOption[]>(() => {
    const ownerIds =
      isDefaultView && !stagedFilters.ownerIds && currentUserId ? [currentUserId] : stagedFilters.ownerIds ?? [];
    return ownerIds.reduce<ComboboxOption[]>((acc, curr) => {
      const match = userComboboxOptions.find((u) => u.value === curr);
      if (match) {
        acc.push(match);
      }

      return acc;
    }, []);
  }, [isDefaultView, currentUserId, stagedFilters.ownerIds, userComboboxOptions]);

  return (
    <div className="space-y-2 font-semibold">
      <h3>{t("Filters")}</h3>
      <div className="space-y-4 font-normal">
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Status")}</span>
          <StringSelect
            value={stagedFilters.status ?? "all"}
            onValueChange={(val) => setFilterField("status", val)}
            options={getStatusOptions()}
            className="w-40"
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Stage")}</span>
          <MultiSelect
            options={getStageOptions()}
            value={stagedFilters.stages ?? []}
            onValueChange={(val) => setFilterField("stages", val.length > 0 ? val : undefined)}
            placeholder={t("Any stage")}
            className="w-64"
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Owner")}</span>
          <Combobox
            value={selectedOwnerOptions}
            onChange={(selected) => {
              const ids = Array.isArray(selected) ? selected.map((s) => s.value) : [];
              setFilterField("ownerIds", ids.length > 0 ? ids : undefined);
            }}
            options={userComboboxOptions}
            multiple
            placeholder={t("Any owner")}
            searchPlaceholder={t("Search users...")}
            clearable
            className="w-64"
          />
        </div>
        <DateRangeField
          label={t("Due Date")}
          value={stagedFilters.dueDateRange ?? {}}
          onChange={(val) => setFilterField("dueDateRange", Object.keys(val).length > 0 ? val : undefined)}
        />
        <DateRangeField
          label={t("Closed Date")}
          value={stagedFilters.closedDateRange ?? {}}
          onChange={(val) => setFilterField("closedDateRange", Object.keys(val).length > 0 ? val : undefined)}
        />
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Tags")}</span>
          <div className="flex items-center gap-2">
            <MultiSelect
              options={tagMultiSelectOptions}
              value={stagedFilters.tags?.tags ?? []}
              onValueChange={(tags) =>
                onStagedFiltersChange({
                  ...stagedFilters,
                  tags: tags.length > 0 ? { tags, behavior: stagedFilters.tags?.behavior ?? "match_any" } : undefined,
                })
              }
              placeholder={t("Enter tags...")}
              className="w-64"
              creator={{
                render: (inputValue) => <span className="text-sm">{t('Add "{{tag}}"', { tag: inputValue })}</span>,
                isValidNewOption: (inputValue) => inputValue.trim().length > 0,
                transform: (inputValue) => inputValue.trim(),
                onCreate: (newOption) => {
                  const existingTags = stagedFilters.tags?.tags ?? [];
                  const tags = [...existingTags, newOption.value];
                  onStagedFiltersChange({
                    ...stagedFilters,
                    tags: { tags, behavior: stagedFilters.tags?.behavior ?? "match_any" },
                  });
                },
              }}
            />
            {(stagedFilters.tags?.tags?.length ?? 0) > 0 && (
              <StringSelect
                value={stagedFilters.tags?.behavior ?? "match_any"}
                onValueChange={(val) =>
                  onStagedFiltersChange({
                    ...stagedFilters,
                    tags: { tags: stagedFilters.tags?.tags ?? [], behavior: val },
                  })
                }
                options={getTagsBehaviorOptions()}
                className="w-36"
              />
            )}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t("Custom Filters")}</p>
          {(stagedFilters.freeform ?? []).length > 0 && (
            <div className="space-y-2">
              {(stagedFilters.freeform ?? []).map((filter, index) => (
                <FreeformFilterRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  filter={filter}
                  fieldOptions={getTicketFreeformFieldOptions()}
                  onChange={(updated) => handleUpdateFreeformFilter(index, updated)}
                  onRemove={() => handleRemoveFreeformFilter(index)}
                />
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleAddFreeformFilter}>
            <Plus className="h-4 w-4" />
            {t("Add Filter")}
          </Button>
        </div>
      </div>
    </div>
  );
};
